import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Eye, EyeOff, ArrowLeft, CheckCircle2 } from "lucide-react";
import CountryCodePicker from "./public/CountryCodePicker";
import { DEFAULT_COUNTRY } from "../data/countries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// E.164: leading '+', country digit 1-9, then 7-14 digits.
const E164 = /^\+[1-9]\d{7,14}$/;

export default function AuthView({ onLogin, defaultMode = "login", onBackToLanding }) {
  const [isRegister, setIsRegister] = useState(defaultMode === "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState(""); // local digits only; combined with country.dialCode at submit
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Full E.164 number that gets sent to the backend
  const fullPhone = `${country.dialCode}${phone.replace(/\D/g, "")}`;

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [devMockCode, setDevMockCode] = useState(""); // DEV MOCK — shown on-screen

  // Reset OTP if the user edits the phone or country after verification
  const resetOtpState = () => {
    if (otpSent || otpVerified || otpToken) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpToken("");
      setOtpCode("");
      setDevMockCode("");
    }
  };
  const onPhoneChange = (v) => {
    setPhone(v.replace(/\D/g, "")); // local part is digits only
    resetOtpState();
  };
  const onCountryChange = (c) => {
    setCountry(c);
    resetOtpState();
  };

  const filledFields = [
    firstName.trim(),
    lastName.trim(),
    email.trim(),
    password.length >= 6 ? password : "",
    otpVerified ? "phone" : "",
  ].filter(Boolean).length;
  const regProgress = Math.round((filledFields / 5) * 100);

  const sendOtp = async () => {
    setError("");
    if (!E164.test(fullPhone)) {
      setError(`Enter a valid phone number for ${country.name} (${country.dialCode}).`);
      return;
    }
    setOtpSending(true);
    try {
      const r = await fetch(`${API}/auth/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: fullPhone }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to send code");
      setOtpSent(true);
      setOtpVerified(false);
      setOtpToken("");
      setOtpCode("");
      setDevMockCode(d.code || ""); // DEV MOCK
    } catch (err) {
      setError(err.message);
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    setError("");
    if (!/^\d{6}$/.test(otpCode.trim())) {
      setError("Enter the 6-digit code");
      return;
    }
    setOtpVerifying(true);
    try {
      const r = await fetch(`${API}/auth/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: fullPhone, code: otpCode.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Verification failed");
      setOtpToken(d.otp_token);
      setOtpVerified(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setOtpVerifying(false);
    }
  };

  const canSubmitRegister = isRegister
    ? otpVerified && otpToken && firstName.trim() && lastName.trim() && email.trim() && password.length >= 6
    : true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (isRegister && !otpVerified) {
      setError("Please verify your phone number first.");
      return;
    }
    setLoading(true);
    try {
      const url = isRegister ? `${API}/auth/register` : `${API}/auth/login`;
      const body = isRegister
        ? {
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            phone_number: fullPhone,
            otp_token: otpToken,
          }
        : { email, password };
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const clone = response.clone();
      let data;
      try {
        data = await clone.json();
      } catch {
        data = { detail: "Server error. Please try again." };
      }
      if (!response.ok) throw new Error(data.detail || "Auth failed");
      localStorage.setItem("token", data.token);
      onLogin(data.user, data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0C0D10] border-[#1F2027] text-[#FAFAFA] rounded-none focus:ring-1 focus:ring-[#00FF66] focus:border-[#00FF66] h-11";

  return (
    <div className="auth-container grain-overlay">
      <div className="auth-card animate-fade-in">
        {onBackToLanding && (
          <button
            data-testid="back-to-landing-btn"
            onClick={onBackToLanding}
            className="flex items-center gap-2 text-[#8A8A93] text-sm hover:text-[#FAFAFA] transition-colors mb-6"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        )}

        <div className="flex items-center gap-3 mb-2">
          <img
            src="/aurora-logo.png"
            alt="Aurora"
            className="w-14 h-14 rounded-xl object-cover shadow-lg"
            data-testid="auth-brand-logo"
          />
        </div>
        <p
          className="text-[#8A8A93] text-sm mb-8 uppercase tracking-[0.2em]"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          The New Beginning · FBI-Verified Dating
        </p>

        {error && (
          <div
            data-testid="auth-error"
            className="mb-4 p-3 bg-[#FF2E2E]/10 border border-[#FF2E2E]/30 text-[#FF2E2E] text-sm"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {isRegister && (
            <>
              {/* --- Phone Number field (very first, above First Name) --- */}
              <div data-testid="auth-phone-block">
                <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <div className="flex flex-1 min-w-0">
                    <CountryCodePicker
                      value={country.iso}
                      onChange={onCountryChange}
                      disabled={otpVerified}
                    />
                    <Input
                      data-testid="auth-phone-input"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => onPhoneChange(e.target.value)}
                      placeholder="Your phone number"
                      className={`${inputCls} flex-1 min-w-0 rounded-l-none`}
                      required
                      disabled={otpVerified}
                      aria-label="Phone number (without country code)"
                    />
                  </div>
                  {!otpVerified && (
                    <Button
                      type="button"
                      data-testid="auth-send-otp-btn"
                      onClick={sendOtp}
                      disabled={otpSending || !phone.trim()}
                      className="shrink-0 h-11 px-4 bg-[#1A1A1A] text-white border border-[#3A3A45] hover:bg-[#2A2A32] rounded-none text-xs uppercase tracking-widest"
                    >
                      {otpSending ? "Sending…" : otpSent ? "Resend" : "Send code"}
                    </Button>
                  )}
                  {otpVerified && (
                    <div
                      data-testid="auth-phone-verified-badge"
                      className="shrink-0 h-11 px-3 flex items-center gap-1.5 text-[#00FF66] text-xs uppercase tracking-widest border border-[#00FF66]/40 bg-[#00FF66]/10"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      <CheckCircle2 size={14} /> Verified
                    </div>
                  )}
                </div>

                {otpSent && !otpVerified && (
                  <div data-testid="auth-otp-input-block" className="mt-3">
                    {devMockCode && (
                      <div
                        data-testid="auth-otp-dev-mock"
                        className="mb-2 p-2 bg-[#FFB020]/10 border border-[#FFB020]/40 text-[#FFB020] text-[11px] leading-relaxed"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        DEV MOCK · Your code is <span className="font-bold text-white text-sm">{devMockCode}</span> · In production this arrives via SMS.
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        data-testid="auth-otp-code-input"
                        inputMode="numeric"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="6-digit code"
                        className={`${inputCls} tracking-[0.4em] text-center`}
                      />
                      <Button
                        type="button"
                        data-testid="auth-verify-otp-btn"
                        onClick={verifyOtp}
                        disabled={otpVerifying || otpCode.length !== 6}
                        className="shrink-0 h-11 px-4 bg-[#00FF66] text-black border border-[#00FF66] hover:bg-[#33FF85] rounded-none text-xs uppercase tracking-widest font-semibold"
                      >
                        {otpVerifying ? "Verifying…" : "Verify"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* --- First Name / Last Name --- */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
                    First Name (Real)
                  </Label>
                  <Input
                    data-testid="auth-firstname-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Your real first name"
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
                    Last Name (Real)
                  </Label>
                  <Input
                    data-testid="auth-lastname-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Your real last name"
                    className={inputCls}
                    required
                  />
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
              Email
            </Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputCls}
              required
            />
          </div>
          <div>
            <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
              Password
            </Label>
            <div className="relative">
              <Input
                data-testid="auth-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className={`${inputCls} pr-10`}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A93] hover:text-[#FAFAFA]"
                data-testid="toggle-password"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <Button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading || (isRegister && !canSubmitRegister)}
            className="w-full bg-[#00FF66] text-black font-semibold rounded-none border border-[#00FF66] hover:bg-[#33FF85] h-11 text-sm uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                AUTHENTICATING...
              </span>
            ) : isRegister ? (
              otpVerified ? "Create Account" : "Verify phone to continue"
            ) : (
              "Sign In"
            )}
          </Button>

          {isRegister && (
            <div
              data-testid="auth-progress-nudge"
              className="mt-2 rounded-md border border-[#00FF66]/30 bg-[#00FF66]/[0.04] p-3"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66] font-bold">
                  ⏱  ~45 seconds away from Aurora
                </span>
                <span className="text-[10px] text-[#8A8A93]">{regProgress}%</span>
              </div>

              <div className="h-1 w-full bg-[#1F2027] rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-[#00FF66] to-[#00CC55] transition-all duration-500 ease-out"
                  style={{ width: `${regProgress}%` }}
                />
              </div>

              <ol className="grid grid-cols-4 gap-1 text-center">
                {[
                  { label: "Register", n: 1 },
                  { label: "FBI Check", n: 2 },
                  { label: "Profile", n: 3 },
                  { label: "Match", n: 4 },
                ].map((s) => (
                  <li
                    key={s.n}
                    className={`text-[9px] uppercase tracking-wider py-1.5 rounded-sm border ${
                      s.n === 1
                        ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66] font-bold"
                        : "border-[#1F2027] bg-transparent text-[#5A5C66]"
                    }`}
                  >
                    {s.n === 1 && regProgress === 100 ? "✓ " : ""}
                    {s.label}
                  </li>
                ))}
              </ol>

              <p className="mt-3 text-[10px] text-[#8A8A93] leading-relaxed text-center">
                Join <span className="text-[#FAFAFA] font-semibold">1,000+ verified singles</span> · 100% real, no catfish
              </p>
            </div>
          )}
        </form>

        <div className="mt-6 text-center">
          <button
            data-testid="auth-toggle-btn"
            onClick={() => {
              setIsRegister(!isRegister);
              setError("");
            }}
            className="text-[#8A8A93] text-sm hover:text-[#FAFAFA] transition-colors"
          >
            {isRegister
              ? "Already have an account? Sign in"
              : "New here? Create an account"}
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-[#1F2027]">
          <p className="text-[#8A8A93] text-xs text-center leading-relaxed">
            You must use your <span className="text-[#FF3B6B]">real first and last name</span>. All users pass FBI background verification.
            Fake names or identities will result in permanent ban.
          </p>
        </div>
      </div>
    </div>
  );
}
